import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import {
  COLORS,
  HEADER_DAY_HEIGHT,
  HEADER_HEIGHT,
  HEADER_MONTH_HEIGHT,
} from '../constants';
import { TimelineViewModel } from '../types';
import CornerCell from './CornerCell';
import useDateLocale from '../hooks/useDateLocale';
import { GroupBy } from '../utils/grouping';
import { ZoomLevel } from '../utils/timeScale';
import {
  SortDirection,
  SortKey,
  TimelineColumnKey,
  TimelineSort,
} from '../utils/timelineColumns';

type Props = {
  vm: TimelineViewModel;
  scrollRef: RefObject<HTMLDivElement>;
  groupBy: GroupBy;
  onChangeGroupBy: (next: GroupBy) => void;
  zoom: ZoomLevel;
  onChangeZoom: (next: ZoomLevel) => void;
  // total sticky-left width + the optional columns shown there (for the header row).
  leftWidth: number;
  extraColumns: TimelineColumnKey[];
  // Resizable per-column widths ('plate' + each extra) + drag handler.
  columnWidths: Record<string, number>;
  onColumnResize: (key: string, width: number) => void;
  // Active table-style sort over the left columns (+ setter).
  sort: TimelineSort;
  onSort: (key: SortKey, direction: SortDirection) => void;
};

// Two-row header. Bottom row = one cell per COLUMN (days in Monthly, 6-hour blocks in
// Daily). Top row = group spans (months in Monthly, days in Daily).
//
// MONTHLY only: the month name rides a viewport-PINNED overlay whose X is driven by a
// per-frame scroll transform (mirrors example's CurrentMonthLabel) — that's what kills
// the shake. DAILY day groups are narrow (4 columns) so their date label is rendered
// statically, left-aligned inside the group cell (no floating overlay).
const Header: React.FC<Props> = ({
  vm,
  scrollRef,
  groupBy,
  onChangeGroupBy,
  zoom,
  onChangeZoom,
  leftWidth,
  extraColumns,
  columnWidths,
  onColumnResize,
  sort,
  onSort,
}) => {
  const locale = useDateLocale();
  const labelRefs = useRef<Array<HTMLDivElement | null>>([]);
  const { columnWidth } = vm;
  const isDaily = zoom === 'daily';

  // Locale-proper formatters for the Daily header (admin's language → date order +
  // 12h/24h time). Intl handles ordering per locale (de "08.06."/24h, en "06/08"/12h).
  const localeCode = locale.code || 'en-US';
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
      }),
    [localeCode]
  );
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [localeCode]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    // Floating group label only exists in Monthly (Daily uses static labels).
    if (zoom !== 'monthly') return undefined;
    let raf = 0;

    const update = () => {
      raf = 0;
      const scrollLeft = el.scrollLeft;
      const viewportWidth = el.clientWidth;
      // Day-area is the viewport portion AFTER the sticky left columns (leftWidth).
      const areaCenterLocal = scrollLeft + (viewportWidth - leftWidth) / 2;

      vm.groupSpans.forEach((g, i) => {
        const x = g.startIdx * columnWidth;
        const w = g.count * columnWidth;
        const padding = Math.min(columnWidth * 2, w / 2);
        const leftBound = x + padding;
        const rightBound = x + w - padding;
        const clamped = Math.min(
          Math.max(areaCenterLocal, leftBound),
          rightBound
        );
        const ref = labelRefs.current[i];
        if (ref) {
          // Pinned layer → position in SCREEN coords from one per-frame transform
          // (read with the same scrollLeft as `clamped`) → no sub-pixel shake.
          const screenX = leftWidth + clamped - scrollLeft;
          ref.style.transform = `translate3d(${screenX}px, 0, 0) translate(-50%, 0)`;
        }
      });
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef, vm, leftWidth, columnWidth, zoom]);

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        display: 'flex',
        width: leftWidth + vm.totalWidth,
        height: HEADER_HEIGHT,
        background: COLORS.headerBg,
        boxSizing: 'border-box',
      }}
    >
      {/* Monthly only: floating month labels on a viewport-pinned layer. */}
      {!isDaily && (
        <div
          aria-hidden
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            flex: '0 0 0px',
            alignSelf: 'flex-start',
            width: 0,
            height: HEADER_MONTH_HEIGHT,
            overflow: 'visible',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {vm.groupSpans.map((g, i) => (
            <div
              // eslint-disable-next-line no-return-assign
              ref={(node) => (labelRefs.current[i] = node)}
              key={`label-${i}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                paddingTop: 8,
                transform: 'translate(-50%, 0)',
                willChange: 'transform',
                fontSize: 14,
                fontWeight: 500,
                color: COLORS.monthLabelText,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              {format(g.anchor, 'MMMM yyyy', { locale })}
            </div>
          ))}
        </div>
      )}

      <CornerCell
        groupBy={groupBy}
        onChangeGroupBy={onChangeGroupBy}
        zoom={zoom}
        onChangeZoom={onChangeZoom}
        leftWidth={leftWidth}
        extraColumns={extraColumns}
        columnWidths={columnWidths}
        onColumnResize={onColumnResize}
        sort={sort}
        onSort={onSort}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          flex: '0 0 auto',
          width: vm.totalWidth,
        }}
      >
        {/* Top group row — months (Monthly) or days (Daily). Daily shows the date
            label left-aligned inside the cell; Monthly's month label is the overlay. */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: HEADER_MONTH_HEIGHT,
            background: COLORS.monthRowBg,
          }}
        >
          {vm.groupSpans.map((g, i) => (
            <div
              key={`bg-${i}`}
              style={{
                width: g.count * columnWidth,
                flex: '0 0 auto',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: isDaily ? 8 : 0,
                borderRight:
                  i < vm.groupSpans.length - 1
                    ? `1px solid ${COLORS.monthRowBorder}`
                    : 'none',
                borderBottom: `1px solid ${COLORS.monthRowBorder}`,
              }}
            >
              {isDaily && (
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: COLORS.monthLabelText,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dateFmt.format(g.anchor)}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Bottom column row — days (Monthly: centred weekday + number) or 6-hour
            blocks (Daily: left-aligned time with a clock-tick line). Today's
            column(s) get the bg + 3px top accent. */}
        <div style={{ display: 'flex', height: HEADER_DAY_HEIGHT }}>
          {vm.columns.map((c, i) => {
            const isToday = Math.floor(i / vm.columnsPerDay) === vm.todayIdx;
            const textColor = isToday ? COLORS.todayText : COLORS.headerText;
            const todayStyle = {
              background: isToday ? COLORS.todayCellBg : 'transparent',
              borderTop: isToday
                ? `3px solid ${COLORS.todayAccent}`
                : '3px solid transparent',
            };
            if (isDaily) {
              // First column of each day (00:00): its tick grows up through the
              // day-label row, drawing a full-height vertical divider that ties the
              // day boundary to its date label. Other ticks (06/12/18h) stay short.
              const isStartOfDay = i % vm.columnsPerDay === 0;
              return (
                <div
                  key={vm.columnKeys[i]}
                  style={{
                    width: columnWidth,
                    flex: '0 0 auto',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-end',
                    paddingBottom: 6,
                    paddingLeft: 7,
                    boxSizing: 'border-box',
                    ...todayStyle,
                  }}
                >
                  {/* Clock-tick line: at the column's LEFT edge (aligns with the body
                      gridline / canvas cell start) and anchored to the row bottom.
                      The 00:00 tick extends up past the row into the day-label band. */}
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      width: 1,
                      height: isStartOfDay
                        ? HEADER_DAY_HEIGHT + HEADER_MONTH_HEIGHT
                        : 14,
                      background: isToday
                        ? COLORS.todayAccent
                        : COLORS.gridBorder,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 400,
                      lineHeight: 1,
                      color: textColor,
                    }}
                  >
                    {timeFmt.format(c)}
                  </span>
                </div>
              );
            }
            return (
              <div
                key={vm.columnKeys[i]}
                style={{
                  width: columnWidth,
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  boxSizing: 'border-box',
                  ...todayStyle,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: textColor,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: columnWidth - 8,
                  }}
                >
                  {format(c, 'EEE', { locale })}
                </span>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 400,
                    lineHeight: 1,
                    color: textColor,
                  }}
                >
                  {c.getDate()}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Header;
